// Client-side persistence for favorites, compare, saved searches and recently viewed.
// Kept intentionally tiny and framework-free so it works in SSR + tests.

const KEYS = {
  fav: "pp_favorites",
  cmp: "pp_compare",
  rv: "pp_recently_viewed",
  ss: "pp_saved_searches",
};

function read(key) {
  try { return JSON.parse(localStorage.getItem(key) || "[]"); } catch { return []; }
}
function write(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
  window.dispatchEvent(new CustomEvent("pp:storage", { detail: { key } }));
}

// Favorites — array of listing ids
export const favorites = {
  key: KEYS.fav,
  list: () => read(KEYS.fav),
  has: (id) => read(KEYS.fav).includes(id),
  toggle(id) {
    const list = read(KEYS.fav);
    const i = list.indexOf(id);
    if (i >= 0) list.splice(i, 1); else list.unshift(id);
    write(KEYS.fav, list);
    return list.includes(id);
  },
  clear: () => write(KEYS.fav, []),
};

// Compare — max 4 listing ids
export const compare = {
  key: KEYS.cmp,
  MAX: 4,
  list: () => read(KEYS.cmp),
  has: (id) => read(KEYS.cmp).includes(id),
  toggle(id) {
    const list = read(KEYS.cmp);
    const i = list.indexOf(id);
    if (i >= 0) { list.splice(i, 1); write(KEYS.cmp, list); return { in: false, list }; }
    if (list.length >= 4) return { in: false, list, full: true };
    list.push(id);
    write(KEYS.cmp, list);
    return { in: true, list };
  },
  remove(id) {
    const list = read(KEYS.cmp).filter((x) => x !== id);
    write(KEYS.cmp, list);
    return list;
  },
  clear: () => write(KEYS.cmp, []),
};

// Recently viewed — array of listing ids, most-recent first, capped at 12
export const recentlyViewed = {
  key: KEYS.rv,
  list: () => read(KEYS.rv),
  push(id) {
    const list = read(KEYS.rv).filter((x) => x !== id);
    list.unshift(id);
    write(KEYS.rv, list.slice(0, 12));
  },
};

// Saved searches — array of { id, name, params, created_at }
export const savedSearches = {
  key: KEYS.ss,
  list: () => read(KEYS.ss),
  save(name, params) {
    const list = read(KEYS.ss);
    list.unshift({ id: `s_${Date.now()}`, name, params, created_at: new Date().toISOString() });
    write(KEYS.ss, list.slice(0, 20));
  },
  remove(id) {
    write(KEYS.ss, read(KEYS.ss).filter((s) => s.id !== id));
  },
};

// Hook helper — re-render when storage changes in this tab
import { useEffect, useState } from "react";
export function useLocalList(store) {
  const [list, setList] = useState(store.list());
  useEffect(() => {
    const on = (e) => { if (!e.detail || e.detail.key === store.key) setList(store.list()); };
    window.addEventListener("pp:storage", on);
    window.addEventListener("storage", on);
    return () => {
      window.removeEventListener("pp:storage", on);
      window.removeEventListener("storage", on);
    };
  }, [store]);
  return list;
}
